import { Suspense, lazy } from "react";
import { Navigate, useRoutes } from "react-router-dom";
import { LinearProgress } from "@mui/material";
import { motion } from "framer-motion";
import ThemeContextProvider from "./theme/ThemeContext";
import { useMenu } from "./Context/MenuContext";

import Cookies from "js-cookie";

const LoadingScreen = () => {
  return (
    <div
      style={{
        height: "100vh",
        margin: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <motion.div
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{
          ease: "easeInOut",
          repeat: Infinity,
        }}
      >
        {/* <img
          src="/assets/"
          alt="Logo"
          style={{ width: 120, height: 100 }}
        /> */}
      </motion.div>
    </div>
  );
};

const Loadable = (File) => (props) => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <File {...props} />
    </Suspense>
  );
};

const LoginPage = Loadable(lazy(() => import("./pages/LoginPage")));
const Register = Loadable(lazy(() => import("./pages/Register")));
const ForgetPassword = Loadable(lazy(() => import("./pages/ForgetPassword")));
const ResetPassword = Loadable(lazy(() => import("./pages/ResetPassword")));
const Navbar = Loadable(lazy(() => import("./Components/Navbar/Navbar")));
const Profile = Loadable(lazy(() => import("./pages/ProfilePage")));
const Support = Loadable(lazy(() => import("./pages/SupportPage")));
const Users = Loadable(lazy(() => import("./pages/Users")));
const Devices = Loadable(lazy(() => import("./pages/Devices")));
const DeviceData = Loadable(lazy(() => import("./pages/DeviceData")));
const Dashboard = Loadable(lazy(() => import("./pages/Dashboard")));
const GlobalBmsData = Loadable(lazy(() => import("./pages/GlobalBmsData")));
const GlobalGtiPage = Loadable(lazy(() => import("./pages/GlobalGtiPage")));
const GlobalTabGtiData = Loadable(lazy(() => import("./pages/GlobalTabGtiData")));
const GlobalTabGtiInfo = Loadable(lazy(() => import("./pages/GlobalTabGtiInfo")));
const GlobalTabGtiOndemand = Loadable(lazy(() => import("./pages/GlobalTabGtiOndemand")));
const GlobalTabGtiHeartbeat = Loadable(lazy(() => import("./pages/GlobalTabGtiHeartbeat")));
const GlobalUpsData = Loadable(lazy(() => import("./pages/GlobalUpsData")));
const DynamicReport = Loadable(lazy(() => import("./pages/Reports/DynamicReport")));
const FileUpload = Loadable(lazy(() => import("./pages/FileUpload")));
const SolarSystem = Loadable(lazy(() => import("./pages/SolarPage")));
const SolarInverter = Loadable(lazy(() => import("./pages/SolarDevices")));
const RmsHeartbeat = Loadable(lazy(() => import("./pages/SolarHeartbeat")));
const MeterData = Loadable(lazy(() => import("./pages/MeterData")));
const SolarAnalytics = Loadable(lazy(() => import("./pages/SolarAnalytics")));
const RbacManagement = Loadable(lazy(() => import("./pages/RbacManagement")));
const FotaFirmwareUpload = Loadable(lazy(() => import("./pages/FotaFirmwareUpload")));
const FotaFirmwareRequest = Loadable(lazy(() => import("./pages/FotaFirmwareRequest")));
const FotaFirmwareDetail = Loadable(lazy(() => import("./pages/FotaFirmwareDetail")));
const ConsumerDashboard = Loadable(lazy(() => import("./pages/ConsumerDashboard")));
const Wfm = Loadable(lazy(() => import("./pages/Wfm")));
const NotFound = Loadable(lazy(() => import("./pages/NotFound")));

const ALWAYS_ALLOWED_PATHS = ["profile", "support", "dashboard", "consumerdashboard", "rbacmanagement", "administration", "fotafirmwareupload", "fotafirmwarerequest", "fotafirmwaredetail", "fota", "wfm"];

const normalizePath = (p) => {
  if (!p) return "";
  return String(p)
    .replace(/^\/Genus\//i, "")
    .replace(/^\//, "")
    .split("/")[0]
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
};

// Strict Menu Protection wrapper component
const MenuProtectedRoute = ({ pathName, children: Component }) => {
  const { menuOptions, isMenuLoaded } = useMenu();

  const normPath = normalizePath(pathName);

  // System pages that are always accessible
  if (ALWAYS_ALLOWED_PATHS.includes(normPath)) {
    return Component;
  }

  // Show loading progress until menu API response is received
  if (!isMenuLoaded) {
    return <LinearProgress />;
  }

  // Verify route existence and access in menuOptions
  const isAllowed = menuOptions.some((opt) => {
    if (opt.access === false) return false;

    const optPathNorm = normalizePath(opt.path);
    const optLabelNorm = normalizePath(opt.label);

    return (
      (optPathNorm && optPathNorm === normPath) ||
      (optLabelNorm && optLabelNorm === normPath)
    );
  });

  // Block routes not present in menuOptions or without access permission
  if (!isAllowed) {
    return <NotFound />;
  }

  return Component;
};

export default function PageRoute() {
  const { menuOptions } = useMenu();

  menuOptions.forEach((option) => {
    if (option.children) {
      option.children.forEach((child) => {
        // Process each child route
      });
    }
  });

  const routes = useRoutes([
    {
      path: "/",
      element: (
        <Suspense fallback={<LinearProgress />}>
          <Navigate to="/login" replace />
        </Suspense>
      ),
    },
    {
      path: "/login",
      element: (
        <Suspense fallback={<LinearProgress />}>
          <LoginPage />
        </Suspense>
      ),
    },
    {
      path: "/register",
      element: (
        <Suspense fallback={<LinearProgress />}>
          <Register />
        </Suspense>
      ),
    },
    {
      path: "/forgetPassword",
      element: (
        <Suspense fallback={<LinearProgress />}>
          <ForgetPassword />
        </Suspense>
      ),
    },
    {
      path: "/resetPassword",
      element: (
        <Suspense fallback={<LinearProgress />}>
          <ResetPassword />
        </Suspense>
      ),
    },
    {
      path: "/Genus",
      element: (
        <Suspense fallback={<LinearProgress />}>
          <ThemeContextProvider>
            <Navbar />
          </ThemeContextProvider>
        </Suspense>
      ),
      children: [
        {
          element: (
            <Suspense fallback={<LinearProgress />}>
              {(Cookies.get("user_role") || "").toLowerCase() === "standard" ? (
                <Navigate to="/Genus/ConsumerDashboard" replace />
              ) : (
                <Navigate to="/Genus/Dashboard" replace />
              )}
            </Suspense>
          ),
          index: true,
        },
        {
          path: "ConsumerDashboard",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="ConsumerDashboard">
                <ConsumerDashboard />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "Devices",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Devices">
                <Devices />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "Devices/:deviceid",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Devices">
                <DeviceData />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "Users",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Users">
                <Users />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "Profile",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Profile">
                <Profile />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "Support",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Support">
                <Support />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "Dashboard",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Dashboard">
                <Dashboard />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "BmsDevices",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="BmsDevices">
                <GlobalBmsData />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "Gti",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Gti">
                <GlobalGtiPage />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "GtiData",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Gti">
                <GlobalTabGtiData />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "GtiInfo",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Gti">
                <GlobalTabGtiInfo />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "GtiOndemand",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Gti">
                <GlobalTabGtiOndemand />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "GtiHeartbeat",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Gti">
                <GlobalTabGtiHeartbeat />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "Reports",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Reports">
                <DynamicReport />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "UpsDevices",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="UpsDevices">
                <GlobalUpsData />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "FileUpload",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="FileUpload">
                <FileUpload />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "SolarSystem",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="SolarSystem">
                <SolarSystem />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "SolarInverter",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="SolarInverter">
                <SolarInverter />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "RmsHeartbeat",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="RmsHeartbeat">
                <RmsHeartbeat />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "MeterData",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="MeterData">
                <MeterData />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "SolarAnalytics",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="SolarAnalytics">
                <SolarAnalytics />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "RbacManagement",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="RbacManagement">
                <RbacManagement />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "FotaFirmwareUpload",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="FotaFirmwareUpload">
                <FotaFirmwareUpload />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "FotaFirmwareRequest",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="FotaFirmwareRequest">
                <FotaFirmwareRequest />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "FotaFirmwareDetail/:fotaRequestId",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="FotaFirmwareDetail">
                <FotaFirmwareDetail />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "FotaFirmwareDetail",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="FotaFirmwareDetail">
                <FotaFirmwareDetail />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "Wfm",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <MenuProtectedRoute pathName="Wfm">
                <Wfm />
              </MenuProtectedRoute>
            </Suspense>
          ),
        },
        {
          path: "*",
          element: (
            <Suspense fallback={<LinearProgress />}>
              <NotFound />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: "/404",
      element: (
        <Suspense fallback={<LinearProgress />}>
          <ThemeContextProvider>
            <NotFound />
          </ThemeContextProvider>
        </Suspense>
      ),
    },
    {
      path: "*",
      element: (
        <Suspense fallback={<LinearProgress />}>
          <ThemeContextProvider>
            <NotFound />
          </ThemeContextProvider>
        </Suspense>
      ),
    },
  ]);
  return routes;
}
